import type { ComponentType, LazyExoticComponent } from "react";
import type { LocalizedText } from "../../../app/i18n/i18n";
import { lazyWithChunkError } from "../../../app/router/lazyWithChunkError";
import { CenterComCreateRunFields } from "./centercom/CenterComCreateRunFields";
import { IcmCreateRunFields } from "./icm/IcmCreateRunFields";

export interface ImportAdapterCreateFieldsProps {
  /** Im Anlegedialog gewählter Zielmandant; leer, solange keiner gewählt ist. */
  tenantId: string | null;
}

/** Eingaben des Anlegedialogs: Rahmenfelder plus die Felder des gewählten Adapters. */
export interface ImportAdapterCreateValues {
  tenantId: string;
  displayName?: string | null;
  [adapterField: string]: unknown;
}

/**
 * Ein Wizard-Schritt des Laufrahmens. Der Rahmen zählt die Indizes in Reihenfolge; die Fachseite
 * bildet ihre eigenen Schritte darauf ab (CenterCom: sechs Fachschritte auf fünf Wizard-Schritte).
 */
export interface ImportAdapterStep {
  key: string;
  i18nKey: LocalizedText;
}

export interface ImportAdapterDefinition {
  id: string;
  i18nKey: LocalizedText;
  /** Basis der Fachendpunkte: `/api/v1/admin/import-adapters/<id>`. */
  apiBase: string;
  /** Präfix der Jobtypen des Adapters (`CenterComImport.`); Filter, Overlay und Reload hängen daran. */
  jobTypePrefix: string;
  steps: ImportAdapterStep[];
  /** Wartungsaktionen der Laufliste, die nur dieser Adapter anbietet. */
  maintenance: { resetStatus: boolean; clearUploads: boolean };
  /** Beschriftung des Umfangs in der Laufliste; `importMode` ist nur bei CenterCom gesetzt. */
  importModeLabelKey: (importMode: string | null | undefined) => LocalizedText;
  /** Platzhalter der Bezeichnung im Anlegedialog. */
  displayNamePlaceholderKey: LocalizedText;
  /** Adapterfelder des Anlegedialogs. */
  CreateFields: ComponentType<ImportAdapterCreateFieldsProps>;
  /** Body des `POST <apiBase>/runs` aus den Dialogwerten. */
  createRunBody: (values: ImportAdapterCreateValues) => Record<string, unknown>;
  RunPage: LazyExoticComponent<ComponentType>;
}

export const IMPORT_ADAPTER_WIZARD_STEPS: ImportAdapterStep[] = [
  { key: "upload", i18nKey: "Import adapter step upload" },
  { key: "verify", i18nKey: "Import adapter step verify" },
  { key: "decisions", i18nKey: "Import adapter step decisions" },
  { key: "precheck", i18nKey: "Import adapter step precheck" },
  { key: "import", i18nKey: "Import adapter step import" }
];

export const IMPORT_ADAPTERS: ImportAdapterDefinition[] = [
  {
    id: "centercom",
    i18nKey: "Import adapter centercom",
    apiBase: "/api/v1/admin/import-adapters/centercom",
    jobTypePrefix: "CenterComImport.",
    steps: IMPORT_ADAPTER_WIZARD_STEPS,
    maintenance: { resetStatus: true, clearUploads: true },
    importModeLabelKey: (importMode) => `CenterCom import mode ${importMode || "Full"}`,
    displayNamePlaceholderKey: "CenterCom import display name placeholder",
    CreateFields: CenterComCreateRunFields,
    createRunBody: (values) => ({ tenantId: values.tenantId, displayName: values.displayName ?? null, importMode: values.importMode }),
    RunPage: lazyWithChunkError(() => import("./centercom/AdminCenterComImportRunPage").then((m) => ({ default: m.AdminCenterComImportRunPage })))
  },
  {
    id: "icm",
    i18nKey: "Import adapter icm",
    apiBase: "/api/v1/admin/import-adapters/icm",
    jobTypePrefix: "IcmImport.",
    steps: IMPORT_ADAPTER_WIZARD_STEPS,
    maintenance: { resetStatus: true, clearUploads: false },
    importModeLabelKey: () => "ICM import mode none",
    displayNamePlaceholderKey: "ICM import display name placeholder",
    CreateFields: IcmCreateRunFields,
    createRunBody: (values) => ({ tenantId: values.tenantId, displayName: values.displayName ?? null, centerComImportRunId: values.centerComImportRunId }),
    RunPage: lazyWithChunkError(() => import("./icm/AdminIcmImportRunPage").then((m) => ({ default: m.AdminIcmImportRunPage })))
  }
];

export const DEFAULT_IMPORT_ADAPTER_ID = IMPORT_ADAPTERS[0]?.id ?? "centercom";

export function findImportAdapter(id: string | null | undefined): ImportAdapterDefinition | undefined {
  const normalized = (id ?? "").trim().toLowerCase();
  if (!normalized) return undefined;
  return IMPORT_ADAPTERS.find((a) => a.id.toLowerCase() === normalized);
}

/** Adapterneutrale Laufendpunkte (Liste, Kopf, Schritt, Bezeichnung, Duplizieren, Löschen, Rollback). */
export function importAdapterRunUrl(runId: string, suffix = "") {
  return `/api/v1/admin/import-adapters/runs/${encodeURIComponent(runId)}${suffix}`;
}

export const IMPORT_ADAPTER_RUNS_URL = "/api/v1/admin/import-adapters/runs";

/** Fachendpunkte eines Adapters. */
export function importAdapterApiRunUrl(adapter: Pick<ImportAdapterDefinition, "apiBase">, runId: string, suffix = "") {
  return `${adapter.apiBase}/runs/${encodeURIComponent(runId)}${suffix}`;
}
